import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

/* ===== P&L Calendar — daily net P&L, Indian (fib book) + Crypto (all books) =====
   Month grid, IST days (both books stamp UTC; bucketing converts to IST so a
   crypto exit at 20:00 UTC lands on the next IST day, matching the app's clock).
   Indian = closed monitor trades (netPnl, bucketed by exitTime).
   Crypto = closed trader trades (pnl_inr, bucketed by exit_time).
   Cell click -> drawer with that day's trades. Weekly totals on the right. */

const IST = "Asia/Kolkata";
const dayKey = (iso) => {
  try { const d = new Date(iso); return isNaN(d) ? null : d.toLocaleDateString("en-CA", { timeZone: IST }); }
  catch { return null; }
};
const rup = (n) => `${n < 0 ? "−" : "+"}₹${Math.abs(Math.round(n)).toLocaleString("en-IN")}`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function PnlCalendar() {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: IST }));
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [market, setMarket] = useState("all"); // all | indian | crypto
  const [indian, setIndian] = useState([]);
  const [crypto, setCrypto] = useState([]);
  const [day, setDay] = useState(null); // "YYYY-MM-DD" drawer

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get("/api/monitor/status", { timeout: 30000 });
        setIndian((r.data?.trades || []).filter(t => t.status === "CLOSED" && t.netPnl != null));
      } catch { /* endpoint down: calendar still renders crypto */ }
      try {
        const r = await axios.get("/api/crypto/trader/status", { timeout: 30000 });
        setCrypto((r.data?.closedTrades || []).filter(t => t.pnl_inr != null));
      } catch { /* ditto */ }
    })();
  }, []);

  // day -> {in: {pnl,n,trades[]}, cr: {...}}
  const byDay = useMemo(() => {
    const m = {};
    const put = (key, side, pnl, t) => {
      if (!key) return;
      const d = (m[key] = m[key] || { in: { pnl: 0, n: 0, trades: [] }, cr: { pnl: 0, n: 0, trades: [] } });
      d[side].pnl += pnl; d[side].n += 1; d[side].trades.push(t);
    };
    for (const t of indian) put(dayKey(t.exitTime || t.time || t.placedAt), "in", t.netPnl || 0, t);
    for (const t of crypto) put(dayKey(t.exit_time), "cr", t.pnl_inr || 0, t);
    return m;
  }, [indian, crypto]);

  const sel = (d) => (market === "indian" ? d.in.pnl : market === "crypto" ? d.cr.pnl : d.in.pnl + d.cr.pnl);
  const seln = (d) => (market === "indian" ? d.in.n : market === "crypto" ? d.cr.n : d.in.n + d.cr.n);

  // month grid: weeks Mon..Sun
  const { y, m } = ym;
  const weeks = useMemo(() => {
    const first = new Date(Date.UTC(y, m, 1));
    const daysIn = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    const lead = (first.getUTCDay() + 6) % 7; // Mon=0
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysIn; d++) cells.push(`${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    while (cells.length % 7) cells.push(null);
    const w = [];
    for (let i = 0; i < cells.length; i += 7) w.push(cells.slice(i, i + 7));
    return w;
  }, [y, m]);

  const monthDays = weeks.flat().filter(Boolean).filter(k => byDay[k] && seln(byDay[k]) > 0);
  const monthPnl = monthDays.reduce((a, k) => a + sel(byDay[k]), 0);
  const green = monthDays.filter(k => sel(byDay[k]) > 0);
  const red = monthDays.filter(k => sel(byDay[k]) < 0);
  const maxAbs = Math.max(1, ...monthDays.map(k => Math.abs(sel(byDay[k]))));
  const best = monthDays.reduce((a, k) => (a == null || sel(byDay[k]) > sel(byDay[a]) ? k : a), null);
  const worst = monthDays.reduce((a, k) => (a == null || sel(byDay[k]) < sel(byDay[a]) ? k : a), null);
  const inMonth = monthDays.reduce((a, k) => a + byDay[k].in.pnl, 0);
  const crMonth = monthDays.reduce((a, k) => a + byDay[k].cr.pnl, 0);

  const nav = (d) => setYm(({ y, m }) => { const t = new Date(y, m + d, 1); return { y: t.getFullYear(), m: t.getMonth() }; });
  const cellBg = (pnl) => {
    if (pnl === 0) return "transparent";
    const a = 0.12 + 0.38 * Math.min(1, Math.abs(pnl) / maxAbs);
    return pnl > 0 ? `rgba(38,166,91,${a})` : `rgba(214,69,65,${a})`;
  };
  const todayKey = new Date().toLocaleDateString("en-CA", { timeZone: IST });

  const dayTrades = day && byDay[day]
    ? [
        ...byDay[day].in.trades.map(t => ({
          mkt: "IN", time: t.exitTime || t.time, label: t.tradingsymbol || `${t.index || ""} ${t.strike || ""}`,
          book: t.strategy || "fib", reason: t.exitReason || "", pnl: t.netPnl || 0, live: t.simulated === false,
        })),
        ...byDay[day].cr.trades.map(t => ({
          mkt: "CR", time: t.exit_time, label: `${t.ref || ""} ${t.symbol}`,
          book: t.book === "OB" ? "1h OB-retest" : t.side === "SHORT" ? "4h short" : t.tf === "1d" ? "1d trail" : `${t.tf} ST-trail`,
          reason: t.exit_reason || "", pnl: t.pnl_inr || 0, live: false,
        })),
      ].sort((a, b) => new Date(a.time) - new Date(b.time))
    : [];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h2>P&amp;L Calendar <span style={{ fontSize: 12, color: "var(--muted2)", fontWeight: 400 }}>daily net P&amp;L · IST days · Indian fib book + all crypto books</span></h2>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", margin: "10px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button className="nav-tab" onClick={() => nav(-1)} title="Previous month">‹</button>
          <span style={{ fontWeight: 600, minWidth: 150, textAlign: "center" }}>{MONTHS[m]} {y}</span>
          <button className="nav-tab" onClick={() => nav(1)} title="Next month">›</button>
          <button className="nav-tab" onClick={() => setYm({ y: now.getFullYear(), m: now.getMonth() })}>Today</button>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {[["all", "All"], ["indian", "Indian"], ["crypto", "Crypto"]].map(([id, lbl]) => (
            <button key={id} className={`nav-tab ${market === id ? "active" : ""}`} onClick={() => setMarket(id)}>{lbl}</button>
          ))}
        </div>
        <div style={{ fontSize: 12, color: "var(--muted2)", display: "flex", gap: 14, flexWrap: "wrap" }}>
          <span>Month: <b className={monthPnl >= 0 ? "positive" : "negative"}>{rup(monthPnl)}</b></span>
          {market === "all" && <span>IN {rup(inMonth)} · CR {rup(crMonth)}</span>}
          <span>{monthDays.length} trading days · <span className="positive">{green.length} green</span> / <span className="negative">{red.length} red</span></span>
          {best && <span>best {best.slice(8)}: <span className="positive">{rup(sel(byDay[best]))}</span></span>}
          {worst && <span>worst {worst.slice(8)}: <span className="negative">{rup(sel(byDay[worst]))}</span></span>}
        </div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead>
          <tr>
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => (
              <th key={d} style={{ padding: 6, fontSize: 11, color: "var(--muted2)", textAlign: "center" }}>{d}</th>
            ))}
            <th style={{ padding: 6, fontSize: 11, color: "var(--muted2)", textAlign: "right", width: 110 }}>Week</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => {
            const wk = week.filter(Boolean).filter(k => byDay[k]).reduce((a, k) => a + sel(byDay[k]), 0);
            const wn = week.filter(Boolean).filter(k => byDay[k] && seln(byDay[k]) > 0).length;
            return (
              <tr key={wi}>
                {week.map((k, di) => {
                  if (!k) return <td key={di} style={{ border: "1px solid var(--border, #2a2a2a)", height: 74 }} />;
                  const d = byDay[k];
                  const pnl = d ? sel(d) : 0;
                  const n = d ? seln(d) : 0;
                  return (
                    <td key={di}
                      onClick={() => n > 0 && setDay(k)}
                      title={n > 0 ? "Click for the day's trades" : ""}
                      style={{
                        border: "1px solid var(--border, #2a2a2a)", height: 74, verticalAlign: "top",
                        padding: "4px 6px", cursor: n > 0 ? "pointer" : "default",
                        background: n > 0 ? cellBg(pnl) : "transparent",
                        outline: k === todayKey ? "2px solid var(--accent, #4a90d9)" : "none", outlineOffset: -2,
                      }}>
                      <div style={{ fontSize: 11, color: "var(--muted2)" }}>{Number(k.slice(8))}</div>
                      {n > 0 && (
                        <>
                          <div className={pnl >= 0 ? "positive" : "negative"} style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{rup(pnl)}</div>
                          <div style={{ fontSize: 10, color: "var(--muted2)" }}>
                            {n} trade{n > 1 ? "s" : ""}{market === "all" && d.in.n > 0 && d.cr.n > 0 ? ` · IN ${rup(d.in.pnl)} / CR ${rup(d.cr.pnl)}` : ""}
                          </div>
                        </>
                      )}
                    </td>
                  );
                })}
                <td style={{ border: "1px solid var(--border, #2a2a2a)", padding: "4px 8px", textAlign: "right", verticalAlign: "middle" }}>
                  {wn > 0 && <span className={wk >= 0 ? "positive" : "negative"} style={{ fontSize: 12, fontWeight: 600 }}>{rup(wk)}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 8, lineHeight: 1.6 }}>
        Days bucket by EXIT time converted to IST — a multi-day crypto trade counts on the day it closed, with its full net P&amp;L (booked legs + runner − all charges).
        Indian = closed fib-book trades (netPnl, live + sim as recorded). Crypto = all six paper books (pnl_inr). ORB-Gap and other paper labs are not included.
        Cell shade scales with the day's magnitude within the month; blue outline = today.
      </div>

      {day && byDay[day] && (
        <div style={{
          position: "fixed", top: 84, right: 16, width: "min(560px, 94vw)", maxHeight: "78vh", overflowY: "auto",
          background: "var(--panel, #16181d)", border: "1px solid var(--border, #2a2a2a)", borderRadius: 10,
          boxShadow: "0 8px 30px rgba(0,0,0,.5)", zIndex: 1200, padding: 14,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <b>{new Date(day + "T12:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
              {" — "}<span className={sel(byDay[day]) >= 0 ? "positive" : "negative"}>{rup(sel(byDay[day]))}</span></b>
            <button className="nav-tab" onClick={() => setDay(null)}>✕</button>
          </div>
          <table style={{ width: "100%", fontSize: 12 }}>
            <thead><tr>
              <th style={{ textAlign: "left" }}>Exit (IST)</th><th style={{ textAlign: "left" }}>Mkt</th>
              <th style={{ textAlign: "left" }}>Trade</th><th style={{ textAlign: "left" }}>Book</th>
              <th style={{ textAlign: "left" }}>Exit as</th><th style={{ textAlign: "right" }}>Net</th>
            </tr></thead>
            <tbody>
              {dayTrades.map((t, i) => (
                <tr key={i}>
                  <td style={{ whiteSpace: "nowrap" }}>{new Date(t.time).toLocaleTimeString("en-IN", { timeZone: IST, hour: "2-digit", minute: "2-digit", hour12: false })}</td>
                  <td>{t.mkt}{t.live ? " 🔴" : ""}</td>
                  <td style={{ fontSize: 11 }}>{t.label}</td>
                  <td style={{ fontSize: 10.5, color: "var(--muted2)" }}>{t.book}</td>
                  <td style={{ fontSize: 10.5 }}>{t.reason}</td>
                  <td className={t.pnl >= 0 ? "positive" : "negative"} style={{ textAlign: "right", whiteSpace: "nowrap" }}>{rup(t.pnl)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
